// ─── JourneyPhasesCard ──────────────────────────────────────────────────
// V97.17.3 — Cockpit Parcours therapeutique (5 phases microbiote).
//
// Pensé pour vivre dans SuiviCockpitTimeline sur la page Suivi etape 8
// (palette claire ivoire / vert sombre, cf charte journey.css).
//
// La frise reste factuelle. Une phase suivante est préparée via le parent;
// ce composant ne la rend jamais active et n'envoie aucune notification.
//
// Composant autonome : consomme consultation.protocol_phases et appelle
// onSavePhases(newProtocolPhases) pour l'initialisation et
// onPrepareNextPhase(context) pour une évolution. Le parent gère la
// persistance et les preuves de remise. Aucun side effect ici.
//
// 3 etats UI :
//   1. Pas configure → banner suggestion template + bouton "Initialiser"
//   2. Configure, aucune phase active → bouton "Demarrer le parcours"
//   3. Configure, phase active → hero + timeline horizontale + actions
//
// Cf spec : memory `spec_v2_parcours_home_permanente_2026_05_16.md`.

import { useEffect, useMemo, useState } from "react";
import {
  getActivePhase,
  getActivePhaseWeek,
  startParcours,
} from "../services/protocolPhases";
import { riskFlagLabelFr } from "../services/riskProfileFlags";
import { debutPhaseActive, finPhase } from "../suivi/services/suiviPhaseDates";
import useDateActivationSuivi from "../suivi/services/useDateActivationSuivi";

export async function requestNextPhasePreparation({
  protocolPhases,
  activePhase,
  nextPhase,
  safetyConfirmed = false,
  safetySnapshot = null,
  onPrepareNextPhase,
}) {
  if (!protocolPhases || !activePhase || !nextPhase || !onPrepareNextPhase) return false;
  await onPrepareNextPhase({
    protocolPhases,
    fromPhase: activePhase,
    toPhase: nextPhase,
    safetyConfirmed,
    safetySnapshot,
  });
  return true;
}

export function PhaseTransitionSafetySnapshotSummary({ snapshot, issue = null }) {
  if (!snapshot) {
    return (
      <div role="alert" style={{ fontSize: 12, lineHeight: 1.45, color: "#9a3d32" }}>
        Compléter la fiche cliente — {issue || "le contexte de sécurité structuré est incomplet."}
      </div>
    );
  }
  return (
    <div style={{ padding: "10px 12px", borderRadius: 8, background: "#f4f7f2", fontSize: 12, lineHeight: 1.55, color: "#314437" }}>
      <div><strong>Grossesse :</strong> {snapshot.grossesseActuelle}</div>
      <div><strong>Allaitement :</strong> {snapshot.allaitement}</div>
      <div><strong>Post-partum :</strong> {snapshot.postPartum}</div>
      <div>
        <strong>Évaluation risques/TCA vérifiée :</strong>{' '}
        {snapshot.flagsProfilRisque.length > 0
          ? snapshot.flagsProfilRisque.map(riskFlagLabelFr).join(', ')
          : 'aucun profil signalé'}
      </div>
    </div>
  );
}

export default function JourneyPhasesCard({
  consultation,
  onSavePhases,
  onPrepareNextPhase,
  nextPhaseDraftReady = false,
  safetySnapshot = null,
  safetySnapshotIssue = null,
  pendingPhases = null,
}) {
  const [saving, setSaving] = useState(false);
  const [confirmingPreparation, setConfirmingPreparation] = useState(false);
  const [safetyConfirmed, setSafetyConfirmed] = useState(false);
  const safetySnapshotFingerprint = useMemo(
    () => JSON.stringify(safetySnapshot),
    [safetySnapshot],
  );

  // Le geste de confirmation porte sur le snapshot exact affiché. Si la fiche
  // cliente change pendant que le panneau est ouvert, Anissa doit confirmer le
  // nouveau snapshot par un nouveau geste explicite.
  useEffect(() => {
    setSafetyConfirmed(false);
  }, [safetySnapshotFingerprint]);

  // V97.39.8 (roadmap 1.1) — Supersede le verrou V97.39.7. Quand aucune
  // consultation n'existe encore (cas pack Bilan : page Suivi sans plan, cf.
  // melissa), "Accepter" n'est plus desactive : le parent persiste les phases
  // dans journey_state.pending_protocol_phases (sans creer de consultation,
  // sans consommer le compteur de pack). On affiche donc ici ces phases en
  // attente comme si elles etaient deja portees : la carte reflete le parcours
  // accepte et l'effet de greffe parent les transferera sur la 1ere
  // consultation creee.
  const protocolPhases = consultation?.protocol_phases || pendingPhases || null;

  const activePhase = useMemo(() => getActivePhase(protocolPhases), [protocolPhases]);
  // Nouveau suivi : phase activée à la publication, sans date stockée dans
  // les phases. Affichage seulement ; une date stockée reste prioritaire.
  const activationSuiviAt = useDateActivationSuivi(consultation);
  const activePhaseStartedAt = debutPhaseActive(protocolPhases, consultation, activationSuiviAt);
  const weekInfo = useMemo(
    () => getActivePhaseWeek(protocolPhases, { startedAt: activePhaseStartedAt }),
    [protocolPhases, activePhaseStartedAt],
  );

  // V97.17.5.3 — HOOKS DOIVENT TOUS ETRE APPELES AVANT TOUT EARLY RETURN.
  // Bug react #310 : cursorPct etait useMemo APRES le early return isEmptyPhases,
  // causant un hook count different entre renders → page noire.
  // cursorPct calcule sa logique en interne depuis protocolPhases (gere null).
  const cursorPct = useMemo(() => {
    if (!protocolPhases?.phases?.length) return 0;
    const phases = protocolPhases.phases;
    const total = phases.length;
    const completed = phases.filter((p) => p.status === "completed").length;
    const idx = phases.findIndex((p) => p.status === "active");

    if (idx < 0) {
      if (completed === total) return 100;
      return 0;
    }
    const phaseWidth = 100 / total;
    let pct = idx * phaseWidth;
    if (weekInfo && activePhase?.duration_weeks_max > 0) {
      const phaseProgress = Math.min(
        1,
        weekInfo.weekNumber / activePhase.duration_weeks_max
      );
      pct += phaseProgress * phaseWidth;
    } else {
      pct += phaseWidth * 0.3;
    }
    return Math.min(100, pct);
  }, [protocolPhases, weekInfo, activePhase]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  async function handleStartParcours() {
    if (saving || !protocolPhases) return;
    setSaving(true);
    try {
      const next = startParcours(protocolPhases);
      await onSavePhases(next);
    } finally {
      setSaving(false);
    }
  }

  async function handlePrepareNextPhase({ confirmed = false } = {}) {
    if (saving || !activePhase || !nextPhase || !onPrepareNextPhase) return;
    if (nextPhaseDraftReady && !confirmed) {
      setConfirmingPreparation(true);
      return;
    }
    setSaving(true);
    try {
      await requestNextPhasePreparation({
        protocolPhases,
        activePhase,
        nextPhase,
        safetyConfirmed: confirmed && safetyConfirmed,
        safetySnapshot,
        onPrepareNextPhase,
      });
      setConfirmingPreparation(false);
      setSafetyConfirmed(false);
    } finally {
      setSaving(false);
    }
  }

  // V97.39.6 — Option A : marquer "pas de parcours" pour cette cliente.
  // Range la carte en une ligne (au lieu de pousser un parcours non pertinent
  // pour une cliente nutrition simple). Stocke { skipped: true } : inoffensif
  // en aval (getActivePhase retourne null, le mapper app cliente envoie null).
  async function handleSkipParcours() {
    if (saving) return;
    setSaving(true);
    try {
      await onSavePhases({ skipped: true });
    } finally {
      setSaving(false);
    }
  }

  // V97.39.6 — Reactiver depuis l'etat "pas de parcours" : revient a null,
  // ce qui re-affiche la banniere suggestion.
  async function handleReactivateParcours() {
    if (saving) return;
    setSaving(true);
    try {
      await onSavePhases(null);
    } finally {
      setSaving(false);
    }
  }

  // ─── État 1 : pas configuré OU configuré avec 0 phase (cas custom buggy) ──
  // V97.17.5.1 : guard contre `{ template: 'custom', phases: [] }` qui causait
  // une page noire (rendu de la frise sur tableau vide). Fallback sur l'etat
  // "non configure" + permet de re-choisir un template valide.
  // V97.39.6 — Etat "pas de parcours" (Anissa a explicitement ignore).
  // Carte repliee en une ligne discrete + lien pour reactiver. Doit etre
  // teste AVANT isEmptyPhases (sinon le warning "phases manquantes" s'affiche).
  if (protocolPhases?.skipped) {
    return (
      <div style={skippedRowStyle}>
        <span style={skippedTextStyle}>
          Pas de parcours thérapeutique pour cette cliente.
        </span>
        <button
          type="button"
          disabled={saving}
          onClick={handleReactivateParcours}
          style={skippedLinkStyle(saving)}
        >
          {saving ? "…" : "Revenir a valider"}
        </button>
      </div>
    );
  }

  const isEmptyPhases =
    protocolPhases && (!Array.isArray(protocolPhases.phases) || protocolPhases.phases.length === 0);

  if (!protocolPhases || isEmptyPhases) {
    return (
      <div>
        <div style={suggestionBannerStyle}>
          <div style={suggestionEyebrowStyle}>A VALIDER ANISSA</div>
          <div style={suggestionTitleStyle}>Aucun parcours de phases configure</div>
          <div style={suggestionReasonStyle}>
            Aucun template n'est choisi depuis les analyses ou le pack. Une
            configuration clinique publiee par Anissa est necessaire avant
            d'afficher des phases dans l'application cliente.
          </div>
          <div style={btnRowStyle}>
            <button
              type="button"
              disabled={saving}
              onClick={handleSkipParcours}
              style={skipBtnStyle(saving)}
            >
              Confirmer : pas de parcours de phases
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── État 2 & 3 : configuré ───────────────────────────────────────────

  const totalPhases = protocolPhases.phases.length;
  const completedCount = protocolPhases.phases.filter((p) => p.status === "completed").length;
  const activeIdx = protocolPhases.phases.findIndex((p) => p.status === "active");
  // cursorPct est calcule en haut du composant (avant early return) pour
  // respecter la regle React des hooks (cf V97.17.5.3).

  // V97.43 (lot A — lisibilite temporelle) — Synthese LECTURE SEULE des faits
  // deja calcules (phase active, semaine, temps ecoule, phases terminees,
  // prochaine phase). Aucune ecriture, aucune nouvelle logique : on consolide
  // ce qui est deja rendu plus bas (frise + hero) pour repondre d'un coup d'oeil
  // a « ou en est la cliente ? ». Wording « Vue de travail Anissa » : distingue
  // explicitement l'etat de travail de l'etat publie cote cliente.
  const nextPhase =
    activeIdx >= 0 && activeIdx < totalPhases - 1
      ? protocolPhases.phases[activeIdx + 1]
      : null;
  const showSynthesis = !!activePhase || completedCount > 0;

  return (
    <div>
      {/* Hero phase active (si une est active) */}
      {activePhase && (
        <div style={activeHeroStyle}>
          <div style={suggestionEyebrowStyle}>PHASE ACTIVE</div>
          <div style={activeTitleStyle}>
            Phase {activePhase.order} · {activePhase.client_name}
          </div>
          <div style={activeClinicalStyle}>
            Nom clinique : {activePhase.clinical_name}
          </div>
          {weekInfo && (
            <div style={activeWeekStyle}>
              Semaine {weekInfo.weekNumber}
              {weekInfo.maxWeeks > 0 ? ` sur ~${weekInfo.maxWeeks}` : " (phase ouverte)"}
            </div>
          )}
          {/* V97.17.17 — V97.18 amorce : recommandations cliniques pre-configurees
              de la phase active (depuis protocolPhases.js). Repliable. Pour
              l'instant DONNEES INDICATIVES : pas auto-appliquees au plan, sert
              de cheat-sheet pour Anissa quand elle cree la V suivante. */}
          {activePhase.recommendations && (
            <PhaseRecommendationsBlock recommendations={activePhase.recommendations} />
          )}
        </div>
      )}

      {/* V97.43 (lot A) — Synthese temporelle LECTURE SEULE.
          Consolide phase active / semaine / temps ecoule / phases terminees /
          prochaine phase a partir des faits deja calcules. Aucune ecriture. */}
      {showSynthesis && (
        <div style={synthesisStyle}>
          <div style={synthesisEyebrowStyle}>Vue de travail Anissa</div>
          <div style={synthesisRowStyle}>
            {activePhase ? (
              <span style={synthesisMainStyle}>
                Phase {activePhase.order} active
                {weekInfo
                  ? ` · semaine ${weekInfo.weekNumber}${
                      weekInfo.maxWeeks > 0 ? `/${weekInfo.maxWeeks}` : ""
                    }`
                  : ""}
                {activePhaseStartedAt && (
                  <span style={synthesisSinceStyle}>
                    {" "}
                    · depuis le {formatDate(activePhaseStartedAt)}
                  </span>
                )}
              </span>
            ) : (
              <span style={synthesisMainStyle}>
                {completedCount === totalPhases
                  ? "Parcours terminé"
                  : "Aucune phase active"}
              </span>
            )}
          </div>
          <div style={synthesisMetaRowStyle}>
            <span>
              {completedCount} / {totalPhases} phases terminées
            </span>
            {nextPhase && (
              <span>
                Prochaine : Phase {nextPhase.order} · {nextPhase.client_name}
              </span>
            )}
          </div>
          <div style={synthesisNoteStyle}>
            Les modifications de phases deviennent visibles côté cliente après
            publication du programme.
          </div>
        </div>
      )}

      {/* V97.17.3 — VRAIE TIMELINE HORIZONTALE des phases */}
      <div style={timelineWrapperStyle}>
        <div style={timelineTrackContainerStyle}>
          {/* Segments par phase (base) */}
          <div style={segmentsRowStyle}>
            {protocolPhases.phases.map((phase, idx) => {
              const isCompleted = phase.status === "completed";
              const isActive = phase.status === "active";
              return (
                <div
                  key={phase.id}
                  style={{
                    ...segmentStyle,
                    background: isCompleted
                      ? "rgba(26, 46, 31, 0.45)"
                      : isActive
                      ? "rgba(26, 46, 31, 0.18)"
                      : "rgba(26, 46, 31, 0.05)",
                    borderRight:
                      idx < totalPhases - 1
                        ? "1px solid rgba(255, 255, 255, 0.6)"
                        : "none",
                  }}
                />
              );
            })}
          </div>

          {/* Curseur de progression (Vous etes ici dans la phase) */}
          {activePhase && (
            <div
              style={{
                ...cursorStyle,
                left: `${cursorPct}%`,
              }}
            >
              <div style={cursorDotStyle} />
            </div>
          )}

          {/* Marqueurs ronds entre les phases (separateurs visuels) */}
          {protocolPhases.phases.map((phase, idx) => {
            const isCompleted = phase.status === "completed";
            const isActive = phase.status === "active";
            const leftPct = (idx / totalPhases) * 100;
            return (
              <div
                key={`marker-${phase.id}`}
                style={{
                  ...timelineMarkerStyle,
                  left: `${leftPct}%`,
                  background: isCompleted
                    ? "#1A2E1F"
                    : isActive
                    ? "#1A2E1F"
                    : "white",
                  border: `2px solid ${
                    isCompleted || isActive ? "#1A2E1F" : "rgba(26,46,31,.25)"
                  }`,
                  color: isCompleted || isActive ? "white" : "rgba(26,46,31,.4)",
                }}
                title={`Phase ${phase.order} — ${phase.client_name} (${phase.status})`}
              >
                {phase.order}
              </div>
            );
          })}

          {/* Marqueur fin (apres la derniere phase) */}
          <div
            style={{
              ...timelineMarkerStyle,
              left: "100%",
              background: completedCount === totalPhases ? "#1A2E1F" : "white",
              border: "2px dashed rgba(26,46,31,.4)",
              color: "rgba(26,46,31,.4)",
              fontSize: 9,
            }}
            title="Fin du parcours"
          >
            ✓
          </div>
        </div>

        {/* Labels sous la frise (1 par phase, alignés au début de segment) */}
        <div style={labelsRowStyle}>
          {protocolPhases.phases.map((phase, idx) => {
            const isCompleted = phase.status === "completed";
            const isActive = phase.status === "active";
            const leftPct = (idx / totalPhases) * 100 + 100 / totalPhases / 2;
            return (
              <div
                key={`label-${phase.id}`}
                style={{
                  ...phaseLabelStyle,
                  left: `${leftPct}%`,
                  color: isActive
                    ? "#1A2E1F"
                    : isCompleted
                    ? "rgba(26,46,31,.7)"
                    : "rgba(26,46,31,.45)",
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                <div style={phaseLabelTopStyle}>Phase {phase.order}</div>
                <div style={phaseLabelBottomStyle}>{phase.client_name}</div>
                {isActive && weekInfo && (
                  <div style={phaseLabelMetaStyle}>
                    Semaine {weekInfo.weekNumber}
                    {weekInfo.maxWeeks > 0 ? `/${weekInfo.maxWeeks}` : " (ouverte)"}
                  </div>
                )}
                {isCompleted && finPhase(phase, protocolPhases, consultation, activationSuiviAt) && (
                  <div style={phaseLabelMetaStyle}>
                    Terminée {formatDate(finPhase(phase, protocolPhases, consultation, activationSuiviAt))}
                  </div>
                )}
                {!isActive && !isCompleted && (
                  <div style={phaseLabelMetaStyle}>
                    {phase.duration_weeks_max > 0
                      ? `~${phase.duration_weeks_max} sem`
                      : "durée ouverte"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions principales (start parcours ou rien) */}
      {!activePhase && completedCount === 0 && (
        <div style={{ ...btnRowStyle, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleStartParcours}
            style={primaryBtnStyle(saving)}
          >
            {saving ? "Démarrage…" : "Démarrer le parcours (phase 1)"}
          </button>
        </div>
      )}

      {activePhase && nextPhase && onPrepareNextPhase && (
        <div style={{ marginTop: 18 }}>
          {!confirmingPreparation ? (
            <div style={{ ...btnRowStyle, alignItems: "center" }}>
              <button
                type="button"
                disabled={saving}
                onClick={() => handlePrepareNextPhase()}
                style={primaryBtnStyle(saving)}
              >
                {saving
                  ? "Préparation…"
                  : (nextPhaseDraftReady
                    ? "Préparer la remise de la phase suivante"
                    : "Créer le programme de la phase suivante")}
              </button>
              <span style={prepareHintStyle}>
                {nextPhaseDraftReady
                  ? "La version relue sera verrouillée avant l'aperçu et la remise."
                  : "Une nouvelle version s'ouvre pour qu'Anissa l'adapte et la relise d'abord."}
              </span>
            </div>
          ) : (
            <div style={preparationConfirmStyle}>
              <strong style={{ fontSize: 13, color: "#1A2E1F" }}>
                Validation d'Anissa avant verrouillage
              </strong>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "#4b5f50" }}>
                Confirme que le programme de la phase {nextPhase.order} a été relu et que le contexte de sécurité actuel de la cliente a été vérifié.
              </p>
              <PhaseTransitionSafetySnapshotSummary
                snapshot={safetySnapshot}
                issue={safetySnapshotIssue}
              />
              <label style={preparationCheckboxStyle}>
                <input
                  type="checkbox"
                  checked={safetyConfirmed}
                  disabled={!safetySnapshot}
                  onChange={(event) => setSafetyConfirmed(event.target.checked)}
                />
                Je valide ce programme et le contexte de sécurité pour cette phase.
              </label>
              <div style={btnRowStyle}>
                <button
                  type="button"
                  disabled={saving || !safetyConfirmed || !safetySnapshot}
                  onClick={() => handlePrepareNextPhase({ confirmed: true })}
                  style={primaryBtnStyle(saving || !safetyConfirmed || !safetySnapshot)}
                >
                  {saving ? "Verrouillage…" : "Valider et préparer la remise"}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setConfirmingPreparation(false);
                    setSafetyConfirmed(false);
                  }}
                  style={ghostBtnStyle(saving)}
                >
                  Revenir à l'édition
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── V97.17.17 — Sous-composant : recommandations de la phase active ─────

function PhaseRecommendationsBlock({ recommendations }) {
  const [expanded, setExpanded] = useState(false);
  const r = recommendations || {};
  const hasContent =
    (r.foods_favor?.length || 0) > 0 ||
    (r.foods_limit?.length || 0) > 0 ||
    (r.supplements?.length || 0) > 0 ||
    (r.clinical_notes || '').length > 0;
  if (!hasContent) return null;

  return (
    <div style={recoBlockStyle}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={recoToggleStyle}
      >
        {expanded ? '▾ Masquer' : '▸ Voir'} les recommandations de cette phase
        <span style={recoIndicatorStyle}>indicatif — non auto-appliqué</span>
      </button>

      {expanded && (
        <div style={recoContentStyle}>
          {r.clinical_notes && (
            <p style={recoNotesStyle}>{r.clinical_notes}</p>
          )}

          {r.foods_favor?.length > 0 && (
            <div style={recoSectionStyle}>
              <div style={recoLabelStyle}>À privilégier</div>
              <div style={recoChipsStyle}>
                {r.foods_favor.map((f, i) => (
                  <span key={i} style={recoChipFavorStyle}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {r.foods_limit?.length > 0 && (
            <div style={recoSectionStyle}>
              <div style={recoLabelStyle}>À limiter</div>
              <div style={recoChipsStyle}>
                {r.foods_limit.map((f, i) => (
                  <span key={i} style={recoChipLimitStyle}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {r.supplements?.length > 0 && (
            <div style={recoSectionStyle}>
              <div style={recoLabelStyle}>Suppléments</div>
              <ul style={recoSupplementsListStyle}>
                {r.supplements.map((s, i) => (
                  <li key={i} style={recoSupplementItemStyle}>
                    <span style={recoSupplementNameStyle}>{s.name}</span>
                    {s.dose && <span style={recoSupplementMetaStyle}>{s.dose}</span>}
                    {s.timing && <span style={recoSupplementMetaStyle}>{s.timing}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatDate(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

// ─── Styles (palette claire ivoire / vert sombre) ─────────────────────────

const mutedStyle = {
  fontSize: 13,
  color: "var(--jrn-text-muted, #6b6f6b)",
  margin: "0 0 12px 0",
  lineHeight: 1.55,
};

const suggestionBannerStyle = {
  background: "rgba(26, 46, 31, 0.04)",
  border: "1px solid rgba(26, 46, 31, 0.12)",
  borderRadius: 8,
  padding: "12px 14px",
  marginBottom: 8,
};

const suggestionEyebrowStyle = {
  fontSize: 10,
  letterSpacing: ".12em",
  fontWeight: 700,
  color: "#1A2E1F",
  textTransform: "uppercase",
};

const suggestionTitleStyle = {
  marginTop: 4,
  fontSize: 14,
  color: "#1A2E1F",
  fontWeight: 600,
};

const suggestionReasonStyle = {
  marginTop: 4,
  fontSize: 12,
  color: "var(--jrn-text-muted, #6b6f6b)",
  fontStyle: "italic",
};

const btnRowStyle = {
  marginTop: 10,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const prepareHintStyle = {
  maxWidth: 420,
  fontSize: 11.5,
  lineHeight: 1.45,
  color: "var(--jrn-text-muted, #6b6f6b)",
};

const preparationConfirmStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  maxWidth: 650,
  padding: "14px 16px",
  borderRadius: 10,
  border: "1px solid rgba(46, 94, 62, .28)",
  background: "rgba(234, 243, 236, .72)",
};

const preparationCheckboxStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 9,
  fontSize: 12.5,
  lineHeight: 1.45,
  color: "#1A2E1F",
  cursor: "pointer",
};

const pickerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginTop: 8,
};

const pickerItemStyle = {
  background: "white",
  border: "1px solid rgba(26, 46, 31, 0.12)",
  borderRadius: 7,
  padding: "10px 12px",
  textAlign: "left",
  cursor: "pointer",
  transition: "all 120ms ease",
};

const pickerLabelStyle = {
  fontWeight: 600,
  color: "#1A2E1F",
  fontSize: 13,
};

const pickerDescStyle = {
  marginTop: 2,
  fontSize: 11.5,
  color: "var(--jrn-text-muted, #6b6f6b)",
};

const pickerWarningStyle = {
  fontSize: 12,
  color: "#a04040",
  background: "rgba(160, 64, 64, 0.06)",
  border: "1px solid rgba(160, 64, 64, 0.2)",
  borderRadius: 6,
  padding: "8px 10px",
  marginBottom: 6,
};

function primaryBtnStyle(disabled) {
  return {
    background: disabled ? "rgba(26,46,31,.08)" : "#1A2E1F",
    border: "1px solid #1A2E1F",
    borderRadius: 7,
    padding: "8px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    color: disabled ? "#6b6f6b" : "#FAF9F6",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transition: "all 120ms ease",
  };
}

function ghostBtnStyle(disabled) {
  return {
    background: "transparent",
    border: "1px solid rgba(26, 46, 31, 0.2)",
    borderRadius: 7,
    padding: "8px 14px",
    fontSize: 12.5,
    color: disabled ? "#9b9f9b" : "#1A2E1F",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transition: "all 120ms ease",
  };
}

// V97.39.6 — Bouton "Pas de parcours" : volontairement discret (texte simple,
// pas de bordure) pour ne pas concurrencer Accepter, mais rester accessible.
function skipBtnStyle(disabled) {
  return {
    background: "transparent",
    border: "none",
    padding: "8px 6px",
    fontSize: 12.5,
    color: disabled ? "#9b9f9b" : "var(--jrn-text-muted, #6b6f6b)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    transition: "all 120ms ease",
  };
}

// V97.39.6 — Etat replie "pas de parcours"
const skippedRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  background: "rgba(26, 46, 31, 0.03)",
  border: "1px dashed rgba(26, 46, 31, 0.15)",
  borderRadius: 8,
  flexWrap: "wrap",
};

const skippedTextStyle = {
  fontSize: 12.5,
  color: "var(--jrn-text-muted, #6b6f6b)",
  fontStyle: "italic",
};

function skippedLinkStyle(disabled) {
  return {
    background: "transparent",
    border: "none",
    padding: 0,
    fontSize: 12.5,
    fontWeight: 600,
    color: disabled ? "#9b9f9b" : "#1A2E1F",
    cursor: disabled ? "not-allowed" : "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  };
}

const activeHeroStyle = {
  background: "rgba(26, 46, 31, 0.06)",
  border: "1px solid rgba(26, 46, 31, 0.18)",
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 10,
};

const activeTitleStyle = {
  marginTop: 4,
  fontSize: 15,
  color: "#1A2E1F",
  fontWeight: 700,
};

const activeClinicalStyle = {
  marginTop: 3,
  fontSize: 11.5,
  color: "var(--jrn-text-muted, #6b6f6b)",
  fontStyle: "italic",
};

const activeWeekStyle = {
  marginTop: 8,
  fontSize: 13,
  color: "#1A2E1F",
  fontWeight: 500,
};

const transitionSuggestionStyle = {
  marginTop: 12,
  padding: "10px 12px",
  background: "rgba(184, 134, 38, 0.08)",
  border: "1px solid rgba(184, 134, 38, 0.3)",
  borderRadius: 7,
};

const transitionLabelStyle = {
  fontSize: 12,
  color: "#785a1a",
  lineHeight: 1.4,
};

// V97.17.26 — Style renforce quand pattern positif detecte (vert sage = encourage transition)
const transitionSuggestionEnforcedStyle = {
  marginTop: 12,
  padding: "12px 14px",
  background: "rgba(46, 94, 62, 0.10)",
  border: "1.5px solid rgba(46, 94, 62, 0.45)",
  borderRadius: 7,
};

const transitionLabelEnforcedStyle = {
  fontSize: 12.5,
  color: "#2E5E3E",
  lineHeight: 1.4,
  fontWeight: 600,
};

const transitionDetailStyle = {
  fontSize: 11,
  color: "rgba(46, 94, 62, 0.85)",
  marginTop: 4,
  lineHeight: 1.4,
};

// V97.17.17 — Styles du bloc recommandations phase (V97.18 amorce)
const recoBlockStyle = {
  marginTop: 12,
  padding: "10px 12px",
  background: "rgba(26, 46, 31, 0.03)",
  border: "1px dashed rgba(26, 46, 31, 0.15)",
  borderRadius: 7,
};

const recoToggleStyle = {
  background: "transparent",
  border: "none",
  padding: 0,
  fontSize: 11.5,
  color: "#1A2E1F",
  cursor: "pointer",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontFamily: "inherit",
};

const recoIndicatorStyle = {
  fontSize: 10,
  fontStyle: "italic",
  color: "var(--jrn-text-muted, #6b6f6b)",
  fontWeight: 400,
  letterSpacing: ".02em",
};

const recoContentStyle = {
  marginTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const recoNotesStyle = {
  fontSize: 12,
  fontStyle: "italic",
  color: "#1A2E1F",
  lineHeight: 1.5,
  background: "rgba(26, 46, 31, 0.05)",
  padding: "8px 10px",
  borderRadius: 6,
  margin: 0,
};

const recoSectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
};

const recoLabelStyle = {
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  color: "var(--jrn-text-muted, #6b6f6b)",
};

const recoChipsStyle = {
  display: "flex",
  gap: 5,
  flexWrap: "wrap",
};

const recoChipFavorStyle = {
  fontSize: 11,
  padding: "3px 9px",
  borderRadius: 999,
  background: "rgba(46, 94, 62, 0.10)",
  color: "#2E5E3E",
  fontWeight: 500,
};

const recoChipLimitStyle = {
  fontSize: 11,
  padding: "3px 9px",
  borderRadius: 999,
  background: "rgba(160, 64, 64, 0.08)",
  color: "#a04040",
  fontWeight: 500,
};

const recoSupplementsListStyle = {
  margin: 0,
  paddingLeft: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const recoSupplementItemStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  gap: 8,
  fontSize: 11.5,
  color: "#1A2E1F",
};

const recoSupplementNameStyle = {
  fontWeight: 600,
};

const recoSupplementMetaStyle = {
  fontSize: 10.5,
  color: "var(--jrn-text-muted, #6b6f6b)",
  fontStyle: "italic",
};

// ─── V97.43 (lot A) — Synthese temporelle (lecture seule) ─────────────────

const synthesisStyle = {
  background: "rgba(26, 46, 31, 0.03)",
  border: "1px solid rgba(26, 46, 31, 0.10)",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 12,
};

const synthesisEyebrowStyle = {
  fontSize: 9.5,
  letterSpacing: ".12em",
  fontWeight: 700,
  color: "var(--jrn-text-muted, #6b6f6b)",
  textTransform: "uppercase",
};

const synthesisRowStyle = {
  marginTop: 5,
};

const synthesisMainStyle = {
  fontSize: 13,
  color: "#1A2E1F",
  fontWeight: 600,
};

const synthesisSinceStyle = {
  fontWeight: 400,
  color: "var(--jrn-text-muted, #6b6f6b)",
};

const synthesisMetaRowStyle = {
  marginTop: 4,
  display: "flex",
  gap: 14,
  flexWrap: "wrap",
  fontSize: 11.5,
  color: "var(--jrn-text-muted, #6b6f6b)",
};

const synthesisNoteStyle = {
  marginTop: 8,
  paddingTop: 7,
  borderTop: "1px solid rgba(26, 46, 31, 0.07)",
  fontSize: 10.5,
  fontStyle: "italic",
  color: "var(--jrn-text-muted, #6b6f6b)",
  lineHeight: 1.4,
};

// ─── V97.17.3 — Timeline horizontale styles ───────────────────────────────

const timelineWrapperStyle = {
  position: "relative",
  padding: "22px 16px 80px 16px",
};

const timelineTrackContainerStyle = {
  position: "relative",
  height: 18,
};

const segmentsRowStyle = {
  display: "flex",
  width: "100%",
  height: 8,
  marginTop: 5,
  borderRadius: 4,
  overflow: "hidden",
  background: "rgba(26, 46, 31, 0.03)",
};

const segmentStyle = {
  flex: 1,
  height: "100%",
  transition: "background 240ms ease",
};

const timelineMarkerStyle = {
  position: "absolute",
  top: 0,
  transform: "translateX(-50%)",
  width: 18,
  height: 18,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  zIndex: 2,
};

const cursorStyle = {
  position: "absolute",
  top: -4,
  transform: "translateX(-50%)",
  zIndex: 3,
  pointerEvents: "none",
};

const cursorDotStyle = {
  width: 14,
  height: 14,
  borderRadius: "50%",
  background: "#1A2E1F",
  border: "3px solid #FAF9F6",
  boxShadow: "0 0 0 1px rgba(26,46,31,.5), 0 2px 6px rgba(26,46,31,.3)",
};

const labelsRowStyle = {
  position: "relative",
  height: 60,
  marginTop: 18,
};

const phaseLabelStyle = {
  position: "absolute",
  transform: "translateX(-50%)",
  textAlign: "center",
  fontSize: 11,
  lineHeight: 1.35,
  minWidth: 80,
  maxWidth: 140,
};

const phaseLabelTopStyle = {
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  opacity: 0.7,
};

const phaseLabelBottomStyle = {
  fontSize: 12,
  marginTop: 2,
};

const phaseLabelMetaStyle = {
  fontSize: 10,
  marginTop: 3,
  fontWeight: 400,
  opacity: 0.75,
  fontStyle: "italic",
};
